import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import { userDataStore } from "../../../../../../store/userData";
import { businessFibiClientStore } from "../../../../../../store/businessFibiClient";
import { SessionQuery } from "./types";
import Sidebar from "./FiBiChatSidebar";
import BusinessStarter from "./BusinessStarter";
import BusinessMain from "./BusinessMain";
import RequestWrapper from "../../../../components/RequestWrapper";
import { FibiAiQuery } from "./data";
import {
  useGetBusinessUserSessionData,
  useGetUserSessionData,
} from "../../../../../../hooks/ai/useAI";

const BusinessFibiVoice = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionTemp = searchParams.get(SessionQuery.sessionCount);
  const fibiUserType = searchParams.get(FibiAiQuery.userType);
  const { userData } = useSnapshot(userDataStore);
  const { fibiClientToken } = useSnapshot(businessFibiClientStore);
  const [sidebarItems, setSidebarItems] = useState<
    { label: string; count: number }[]
  >([]);

  const {
    data: uData,
    isLoading: uLoading,
    isError: uIsError,
  } = useGetUserSessionData(!!fibiUserType);
  const {
    data: bData,
    isLoading: bLoading,
    isError: bIsError,
  } = useGetBusinessUserSessionData(fibiClientToken || "", !fibiUserType);

  const data = bData || uData;
  const isLoading = !!fibiUserType ? uLoading : bLoading;
  const isError = bIsError || uIsError;

  const isCompleted = !!(
    sessionTemp &&
    data?.some(
      (item) => item.interviewNo === +sessionTemp && item.status === "COMPLETED"
    )
  );

  const [showMainInterview, setShowMainInterview] = useState<boolean>();
  const [showingAnalysis, setShowingAnalysis] = useState(false);

  const onShowingAnalysis = (second: boolean) => {
    setShowingAnalysis(second);
  };

  const [sessionCompleted, setSessionCompleted] = useState<boolean>(false);

  const changeSessionCompleted = useCallback((second: boolean) => {
    setSessionCompleted(second);
  }, []);

  useEffect(() => {
    if (!userData) return;
    if (userData.currentSessionStartTime !== null) {
      setShowMainInterview(true);
      businessFibiClientStore.storedCurrentSessionStartTime =
        userData.currentSessionStartTime;
    } else {
      if (!showingAnalysis) {
        setShowMainInterview(false);
      }
    }
  }, [userData, showingAnalysis]);

  useEffect(() => {
    if (isCompleted) {
      setShowMainInterview(true);
      setSessionCompleted(true);
    }
  }, [isCompleted]);

  useEffect(() => {
    if (!userData) return;

    const count = userData.interviewCount ?? 1;

    if (sessionTemp) {
      if (+sessionTemp <= count) return;
      const temp = new Array(+sessionTemp).fill(1).map((ev, index) => ({
        label: `Session ${index + 1}`,
        count: index + 1,
      }));

      setSidebarItems(temp);
    } else {
      searchParams.set(SessionQuery.sessionCount, `${count}`);
      setSearchParams(searchParams);

      const temp = new Array(count).fill(1).map((ev, index) => ({
        label: `Session ${index + 1}`,
        count: index + 1,
      }));

      setSidebarItems(temp);
    }
  }, [searchParams, sessionTemp, setSearchParams, userData]);

  useEffect(() => {
    if (!userData) return;

    const count = userData.interviewCount ?? 1;

    if (count > sidebarItems.length) {
      const temp = new Array(count).fill(1).map((ev, index) => ({
        label: `Session ${index + 1}`,
        count: index + 1,
      }));

      setSidebarItems(temp);
    }
  }, [sidebarItems.length, userData]);

  const onStart = () => {
    if (userData && userData.currentSessionStartTime === null) {
      businessFibiClientStore.storedCurrentSessionStartTime = Date.now();
    }
    setShowMainInterview(true);
  };

  const onInterviewOff = () => {
    setShowMainInterview(false);
  };

  return (
    <RequestWrapper
      isLoading={showMainInterview === undefined || isLoading}
      isError={isError || false}
      data={true}
    >
      <div className="relative flex h-full gap-4">
        {(fibiUserType ? true : fibiClientToken) && (
          <Sidebar
            items={[...sidebarItems]}
            sessionCompleted={sessionCompleted}
            onInterviewOff={onInterviewOff}
          />
        )}

        <div className="w-full h-full ">
          {(fibiUserType ? true : fibiClientToken) && !showMainInterview ? (
            <BusinessStarter onStart={onStart} />
          ) : (
            <BusinessMain
              changeSessionCompleted={changeSessionCompleted}
              onInterviewOff={onInterviewOff}
              onShowingAnalysis={onShowingAnalysis}
            />
          )}
        </div>
      </div>
    </RequestWrapper>
  );
};

export default BusinessFibiVoice;
